<template>
  <div class="ase-root" ref="root">

    <!-- Scroll track: 400% de la altura del root (= pista de scroll) -->
    <div class="ase-track" ref="track">

      <!-- Sticky: se queda pegado mientras hay scroll en el root -->
      <div class="ase-sticky">
        <canvas class="ase-canvas" ref="canvas"></canvas>

        <!-- Captions sobre el canvas -->
        <div
          v-for="(cap, i) in captions"
          :key="i"
          class="ase-caption"
          :class="[cap.position, { visible: visibleCaptions[i] }]"
        >
          <h2>{{ cap.title }}</h2>
          <p>{{ cap.body }}</p>
        </div>

        <!-- Loading overlay -->
        <div class="ase-loader" :class="{ hidden: loaderHidden }">
          <span class="ase-loader-text">Cargando</span>
          <div class="ase-bar-wrap">
            <div class="ase-bar" :style="{ width: loadPct + '%' }"></div>
          </div>
        </div>
      </div>

    </div>

    <!-- Hint de scroll -->
    <div class="ase-hint" :class="{ hidden: scrolled }">
      <span>scroll</span>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 1v10M2 8l4 4 4-4" stroke="currentColor" stroke-width="1.3"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

  </div>
</template>

<script>
const TOTAL_FRAMES = 95;

// Webpack importa todos los PNGs del directorio de forma ordenada
const imgCtx = require.context(
  '@/assets/exercises/sequence-effect',
  false,
  /Untitled-\d{4}\.png$/
);
const FRAME_URLS = imgCtx.keys().sort().map(k => imgCtx(k));

export default {
  name: 'AppleScrollEffect',

  data() {
    return {
      frames:         new Array(TOTAL_FRAMES).fill(null),
      rawProgress:    0,
      smoothProgress: 0,
      currentIndex:   0,
      loaderHidden:   false,
      loadPct:        0,
      scrolled:       false,
      captions: [
        { title: 'Diseño que enamora',        body: 'Cada detalle pensado para que la experiencia se sienta mágica.',  position: 'top',    from: 0,  to: 22 },
        { title: 'Potencia real',              body: 'Tecnología de punta bajo una interfaz que cualquiera entiende.', position: 'bottom', from: 25, to: 48 },
        { title: 'Tu flujo. Tu ritmo.',        body: 'Flujos adaptativos que aprenden cómo trabajas.',                  position: 'top',    from: 52, to: 74 },
        { title: 'Listo para lo que viene.',   body: 'Construido para escalar contigo desde el día uno.',               position: 'bottom', from: 78, to: 100 },
      ],
      visibleCaptions: [],
    };
  },

  created() {
    this.visibleCaptions = new Array(this.captions.length).fill(false);
    // Propiedades internas fuera de la reactividad de Vue
    this.dpr            = window.devicePixelRatio || 1;
    this.rafId          = null;
    this.preloadStarted = false;
    this.reducedMotion  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  mounted() {
    this.loadFirstFrame().then(() => {
      this.resizeCanvas();
      this.paintFrame(0);
      this.loaderHidden = true;
      this.startPreload();
      this.startLoop();
    });
    this.onResize = () => this.resizeCanvas();
    window.addEventListener('resize', this.onResize);
  },

  beforeUnmount() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this.onResize);
  },

  methods: {

    // ── PASO 1: Cargar frames ─────────────────────────────────────────

    loadFrame(index) {
      return new Promise(resolve => {
        if (this.frames[index]) { resolve(this.frames[index]); return; }
        const img = new Image();
        img.onload  = () => { this.frames[index] = img; resolve(img); };
        img.onerror = () => resolve(null);
        img.src     = FRAME_URLS[index];
      });
    },

    async loadFirstFrame() {
      await this.loadFrame(0);
    },

    async startPreload() {
      if (this.preloadStarted) return;
      this.preloadStarted = true;
      let loaded = 1;
      const queue = this.buildQueue(this.currentIndex);
      for (const idx of queue) {
        await this.loadFrame(idx);
        loaded++;
        this.loadPct = Math.round((loaded / TOTAL_FRAMES) * 100);
      }
    },

    buildQueue(fromIdx) {
      const queue = [];
      const max = Math.max(fromIdx, TOTAL_FRAMES - 1 - fromIdx);
      for (let r = 1; r <= max; r++) {
        if (fromIdx + r < TOTAL_FRAMES) queue.push(fromIdx + r);
        if (fromIdx - r >= 0)           queue.push(fromIdx - r);
      }
      return queue;
    },

    // ── PASO 2: Canvas (DPR retina) ───────────────────────────────────

    resizeCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      canvas.width  = Math.round(w * this.dpr);
      canvas.height = Math.round(h * this.dpr);
      const ctx = canvas.getContext('2d');
      ctx.scale(this.dpr, this.dpr);
      this.paintFrame(this.currentIndex);
    },

    // ── PASO 3: Scroll progress ───────────────────────────────────────

    getProgress() {
      const root  = this.$refs.root;
      const track = this.$refs.track;
      if (!root || !track) return 0;
      const scrollable = track.offsetHeight - root.offsetHeight;
      return scrollable > 0
        ? Math.min(1, Math.max(0, root.scrollTop / scrollable))
        : 0;
    },

    lerpFn(a, b, t) { return a + (b - a) * t; },

    // ── PASO 4: Pintar frame (object-fit: contain manual) ─────────────

    paintFrame(index) {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const img = this.frames[index];
      if (!img) return;
      const ctx = canvas.getContext('2d');
      const cw = canvas.offsetWidth;
      const ch = canvas.offsetHeight;
      const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
      const dw = img.naturalWidth  * scale;
      const dh = img.naturalHeight * scale;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    },

    // ── PASO 5: Captions ──────────────────────────────────────────────

    updateCaptions(progress) {
      const pct = progress * 100;
      this.visibleCaptions = this.captions.map(c => pct >= c.from && pct <= c.to);
    },

    // ── PASO 6: Loop rAF ──────────────────────────────────────────────

    startLoop() {
      if (this.reducedMotion) {
        const root = this.$refs.root;
        if (!root) return;
        root.addEventListener('scroll', () => {
          const p = this.getProgress();
          if (!this.scrolled && p > 0) this.scrolled = true;
          const target = p > 0.5 ? TOTAL_FRAMES - 1 : 0;
          if (target !== this.currentIndex) {
            this.currentIndex = target;
            this.loadFrame(target).then(() => this.paintFrame(target));
            this.updateCaptions(p);
          }
        }, { passive: true });
        return;
      }

      const loop = () => {
        this.rawProgress    = this.getProgress();
        this.smoothProgress = this.lerpFn(this.smoothProgress, this.rawProgress, 0.15);
        if (!this.scrolled && this.rawProgress > 0.01) this.scrolled = true;

        const target = Math.min(
          TOTAL_FRAMES - 1,
          Math.floor(this.smoothProgress * (TOTAL_FRAMES - 1))
        );

        if (target !== this.currentIndex) {
          this.currentIndex = target;
          this.paintFrame(target);
          this.updateCaptions(this.smoothProgress);
        }

        this.rafId = requestAnimationFrame(loop);
      };

      this.rafId = requestAnimationFrame(loop);
    },
  },
};
</script>

<style scoped>
/* ── Root: el viewport scrollable ── */
.ase-root {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 520px;
  overflow-y: scroll;
  overflow-x: hidden;
  background: #000;
  scrollbar-width: none;
}
.ase-root::-webkit-scrollbar { display: none; }

/* ── Track: 4× la altura del root para dar recorrido de scroll ── */
.ase-track {
  height: 400%;
  position: relative;
}

/* ── Sticky: se pega al top mientras hay scroll en .ase-root ── */
.ase-sticky {
  position: sticky;
  top: 0;
  height: 25%; /* 25% de 400% = 100% de .ase-root */
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* ── Canvas ── */
.ase-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

/* ── Captions ── */
.ase-caption {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.45s ease;
  width: min(440px, 85%);
  color: #f5f5f7;
}
.ase-caption.visible  { opacity: 1; }
.ase-caption.top      { top: 10%; }
.ase-caption.bottom   { bottom: 10%; }

.ase-caption h2 {
  font-size: clamp(1rem, 2.5vw, 1.6rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: 0.35rem;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif;
}
.ase-caption p {
  font-size: clamp(0.7rem, 1.3vw, 0.85rem);
  color: #a1a1a6;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
}

/* ── Loader ── */
.ase-loader {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #000;
  transition: opacity 0.5s ease;
  z-index: 10;
}
.ase-loader.hidden  { opacity: 0; pointer-events: none; }
.ase-loader-text {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6e6e73;
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
}
.ase-bar-wrap {
  width: 100px;
  height: 1.5px;
  background: #1d1d1f;
  border-radius: 2px;
  overflow: hidden;
}
.ase-bar {
  height: 100%;
  background: #2997ff;
  border-radius: 2px;
  transition: width 0.1s linear;
}

/* ── Scroll hint ── */
.ase-hint {
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 9px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #6e6e73;
  pointer-events: none;
  transition: opacity 0.4s ease;
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  animation: aseBounce 2s ease-in-out infinite;
  z-index: 5;
}
.ase-hint.hidden { opacity: 0; }

@keyframes aseBounce {
  0%, 100% { transform: translateX(-50%) translateY(0);   opacity: 0.45; }
  50%       { transform: translateX(-50%) translateY(5px); opacity: 1;    }
}

@media (prefers-reduced-motion: reduce) {
  .ase-caption { transition: opacity 0.8s ease; }
  .ase-hint    { animation: none; }
}
</style>
